import {
  BEAUTY_FILTER_MENU_ITEMS,
  BEAUTY_KEYS,
  BEAUTY_TABLE,
  MAKEUP_KEYS,
  MAKEUP_TABLE,
  MOOD_KEYS,
  MOOD_TABLE,
  STICKER_KEYS,
  STICKER_TABLE,
  BACKGROUND_KEYS,
  BACKGROUND_TABLE,
} from 'constants/beautyFilterTables';
import { BeautyFilterMenuItem, BeautyFilterUsedItem, BeautyItem, MakeupItem, MoodItem, BackgroundItem, StickerItem } from 'types/beautyFilter';
import { baHideBeautyFilterModal, baShowBeautyFilterModal } from 'utils/beautyFilterUtils';
import useBeautyFilterBanubaStore from 'stores/beautyFilter/beautyFilterBanubaStore';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const DEFAULT_MOOD_KEY = 'se_hudson';
const RESET_MOOD_KEY = 'se_none';

interface BeautyFilterState {
  inited: boolean;
  setInited: (inited: boolean) => void;

  beautyFilterModalHandler: any | null;
  isBeautyFilterModalVisible: boolean;
  showBeautyFilterModal: (handler: any) => void;
  hideBeautyFilterModal: () => void;

  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;

  baData: {
    changedCount: number;
    addChangedCount: () => void;
    resetChangedCount: () => void;
    usedItems: { [id: string]: BeautyFilterUsedItem };
    addUsedItem: (item: BeautyFilterUsedItem) => void;
    resetUsedItems: () => void;
  };
  menu: {
    items: BeautyFilterMenuItem[];
    selectedItem: BeautyFilterMenuItem;
    setSelectedItem: (selectedItem: BeautyFilterMenuItem) => void;
  };
  beauty: {
    keys: string[];
    table: { [key: string]: BeautyItem };
    selectedKey: string | null;
    selectedSubKey: string | null;
    setSelectedKey: (selectedKey: string | null) => void;
    setSelectedSubKey: (selectedSubKey: string | null) => void;
    resetSelectedKey: () => void;
    resetSelectedSubKey: () => void;
  };
  makeup: {
    keys: string[];
    table: { [key: string]: MakeupItem };
    selectedKey: string | null;
    selectedSubKey: string | null;
    setSelectedKey: (selectedKey: string | null) => void;
    setSelectedSubKey: (selectedSubKey: string | null) => void;
    resetSelectedKey: () => void;
    resetSelectedSubKey: () => void;
  };
  mood: {
    keys: string[];
    table: { [key: string]: MoodItem };
    selectedKey: string;
    isUsingMood: () => boolean;
    setSelectedKey: (selectedKey: string) => void;
    resetSelectedKey: () => void;
  };
  sticker: {
    keys: string[];
    table: { [key: string]: StickerItem };
    selectedKey: string | null;
    isUsingSticker: () => boolean;
    setSelectedKey: (selectedKey: string | null) => void;
    resetSelectedKey: () => void;
  };
  background: {
    keys: string[];
    table: { [key: string]: BackgroundItem };
    selectedKey: string | null;
    setSelectedKey: (selectedKey: string | null) => void;
    resetSelectedKey: () => void;
  };
}

const useBeautyFilterStore = create<BeautyFilterState>()(
  persist(
    (set, get) => ({
      inited: false,
      setInited: (inited: boolean) => set({ inited }),

      beautyFilterModalHandler: null,
      isBeautyFilterModalVisible: false,
      showBeautyFilterModal: (handler: any) => {
        // Banuba 사용 불가(banubaUnavailable) 판정이면 흐려진 뷰티 탭 대신 첫 사용 가능 탭(무드)으로 연다
        const menu = get().menu;
        const defaultItem = useBeautyFilterBanubaStore.getState().banubaUnavailable
          ? menu.items.find((item) => !item.requiresBanuba) ?? menu.items[0]
          : menu.items[0];
        set({ isBeautyFilterModalVisible: true, beautyFilterModalHandler: handler, menu: { ...menu, selectedItem: defaultItem } });
        baShowBeautyFilterModal();
        get().baData.resetChangedCount();
        get().baData.resetUsedItems();
      },
      hideBeautyFilterModal: () => {
        if (get().beautyFilterModalHandler?.closedCallback) {
          get().beautyFilterModalHandler.closedCallback();
        }
        set({
          isBeautyFilterModalVisible: false,
          beautyFilterModalHandler: null,
          menu: { ...get().menu, selectedItem: BEAUTY_FILTER_MENU_ITEMS[0] },
          beauty: { ...get().beauty, selectedKey: null, selectedSubKey: null },
          makeup: { ...get().makeup, selectedKey: null, selectedSubKey: null },
        });
        baHideBeautyFilterModal();
      },

      isLoading: false,
      setIsLoading: (isLoading: boolean) => set({ isLoading }),

      baData: {
        changedCount: 0,
        addChangedCount: () => set({ baData: { ...get().baData, changedCount: get().baData.changedCount + 1 } }),
        resetChangedCount: () => {
          set({ baData: { ...get().baData, changedCount: 0 } });
        },
        usedItems: {},
        addUsedItem: (item: BeautyFilterUsedItem) => {
          const id = item.subKey ? `${item.category}:${item.key}:${item.subKey}` : `${item.category}:${item.key}`;
          set({ baData: { ...get().baData, usedItems: { ...get().baData.usedItems, [id]: item } } });
        },
        resetUsedItems: () => {
          set({ baData: { ...get().baData, usedItems: {} } });
        },
      },
      menu: {
        items: BEAUTY_FILTER_MENU_ITEMS,
        selectedItem: BEAUTY_FILTER_MENU_ITEMS[0],
        setSelectedItem: (selectedItem: BeautyFilterMenuItem) => set({ menu: { ...get().menu, selectedItem } }),
      },
      beauty: {
        keys: BEAUTY_KEYS,
        table: BEAUTY_TABLE,
        selectedKey: null,
        selectedSubKey: null,
        setSelectedKey: (selectedKey: string | null) => set({ beauty: { ...get().beauty, selectedKey } }),
        setSelectedSubKey: (selectedSubKey: string | null) => {
          if (selectedSubKey !== get().beauty.selectedSubKey) get().baData.addChangedCount();
          set({ beauty: { ...get().beauty, selectedSubKey } });
        },
        resetSelectedKey: () => get().beauty.setSelectedKey(null),
        resetSelectedSubKey: () => get().beauty.setSelectedSubKey(null),
      },
      makeup: {
        keys: MAKEUP_KEYS,
        table: MAKEUP_TABLE,
        selectedKey: null,
        selectedSubKey: null,
        setSelectedKey: (selectedKey: string | null) => set({ makeup: { ...get().makeup, selectedKey } }),
        setSelectedSubKey: (selectedSubKey: string | null) => {
          if (selectedSubKey !== get().makeup.selectedSubKey) get().baData.addChangedCount();
          set({ makeup: { ...get().makeup, selectedSubKey } });
        },
        resetSelectedKey: () => get().makeup.setSelectedKey(null),
        resetSelectedSubKey: () => get().makeup.setSelectedSubKey(null),
      },
      mood: {
        keys: MOOD_KEYS,
        table: MOOD_TABLE,
        selectedKey: DEFAULT_MOOD_KEY,
        isUsingMood: () => get().mood.selectedKey !== RESET_MOOD_KEY,
        setSelectedKey: (selectedKey: string) => {
          if (selectedKey !== get().mood.selectedKey) {
            get().baData.addChangedCount();
            // se_none 은 무드 해제라 사용으로 세지 않는다
            if (selectedKey !== RESET_MOOD_KEY) {
              get().baData.addUsedItem({ category: 'mood', key: selectedKey, subKey: null, value: null });
            }
          }
          set({ mood: { ...get().mood, selectedKey } });
        },
        resetSelectedKey: () => get().mood.setSelectedKey(RESET_MOOD_KEY),
      },
      sticker: {
        keys: STICKER_KEYS,
        table: STICKER_TABLE,
        selectedKey: null,
        isUsingSticker: () => get().sticker.selectedKey !== null,
        setSelectedKey: (selectedKey: string | null) => {
          if (selectedKey !== get().sticker.selectedKey) {
            get().baData.addChangedCount();
            // null 은 스티커 해제라 사용으로 세지 않는다
            if (selectedKey !== null) {
              get().baData.addUsedItem({ category: 'sticker', key: selectedKey, subKey: null, value: null });
            }
          }
          set({ sticker: { ...get().sticker, selectedKey } });
        },
        resetSelectedKey: () => get().sticker.setSelectedKey(null),
      },
      background: {
        keys: BACKGROUND_KEYS,
        table: BACKGROUND_TABLE,
        selectedKey: null,
        setSelectedKey: (selectedKey: string | null) => set({ background: { ...get().background, selectedKey } }),
        resetSelectedKey: () => get().background.setSelectedKey(null),
      },
    }),
    {
      name: 'beautyFilterStore',
      partialize: (state) => ({
        mood: {
          selectedKey: state.mood.selectedKey,
        },
        sticker: {
          selectedKey: state.sticker.selectedKey,
        },
      }),
      merge: (persistedState: any, currentState) => {
        return {
          ...currentState,
          mood: {
            ...currentState.mood,
            ...persistedState.mood,
          },
          sticker: {
            ...currentState.sticker,
            ...persistedState.sticker,
          },
        };
      },
    }
  )
);

export default useBeautyFilterStore;
